import React, { useState } from 'react';
import moment from 'moment/moment';
import { Modal, Form, Select, Button, SelectOption } from 'tea-component';
import { useForm, useField } from 'react-final-form-hooks';
import IconButton from '@gp/components/IconButton';
import MilestonesTimelines from '@gp/pages/Pipeline/ProjectDetails/components/MilestonesTimelines';
import { updateProjectKeyMilestones } from '@gp/services/projectDetail';
import { isEmptyValue } from '@home/utils/common';
import { formatDateStr, formatTeaFormItemStatus, formatTeaFormItemMessage } from '@gp/utils/common';
import { getKeyMilestones } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { FIELD_COMMON_ERR_MSG } from '@gp/constants/const';

import type { IBaseProps } from '@home/types/common';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';

const DEFINE_NEXT_MILESTONE = 'Set next Milestone';

interface IUpdateNextMilestoneFormModel {
  next_milestone_id: string;
}

interface IUpdateNextMilestoneProps extends IBaseProps {
  projectId: string;
  studioId: string;
  stage?: string;
  // 当前project下的Milestone集合
  milestones?: IMilestone[];
  // 当前project的Next Milestone
  nextMilestone?: IMilestone | null;
  // 更新Next Milestone的回调
  onUpdateNextMilestoneSuccess?: (payload: string) => void;
}

const DEFAULT_FORM_MODEL: IUpdateNextMilestoneFormModel = {
  next_milestone_id: '',
};

const getMilestoneOptions = (milestones?: IMilestone[]): SelectOption[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  return milestones.map((milestone) => {
    const { milestone_id: milestoneId, milestone: milestoneName } = milestone || {};
    return { value: milestoneId, text: milestoneName };
  });
};

const getMilestoneTimelines = (milestones?: IMilestone[]): any[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  return milestones.map((milestone) => {
    const { milestone_id: milestoneId, date: milestoneDate, milestone: milestoneName } = milestone || {};
    const milestoneDateTrim = milestoneDate?.trim();
    const milestoneDateM: moment.Moment | null = !!milestoneDateTrim ? moment(milestoneDateTrim) : null;
    const milestoneDateFormatted = milestoneDateM?.isValid()
      ? milestoneDateM.format('YYYY-MM')
      : milestoneDateTrim || '';
    return { ...milestone, id: milestoneId, label: milestoneDateFormatted, content: milestoneName || '' };
  });
};

const getKeyMilestonesData = (
  milestones?: IMilestone[],
  currentMilestoneId?: string,
  nextMilestoneId?: string,
): {
  isSame: boolean;
  currentMilestone: IMilestone | null;
  currentMilestoneIdx: number;
  nextMilestone: IMilestone | null;
  nextMilestoneIdx: number;
} => {
  if (!Array.isArray(milestones) || !milestones.length)
    return {
      isSame: true,
      currentMilestone: null,
      currentMilestoneIdx: -1,
      nextMilestone: null,
      nextMilestoneIdx: -1,
    };
  const { currentMilestone, nextMilestone } = getKeyMilestones(milestones, currentMilestoneId, nextMilestoneId);
  const currentMilestoneIdx = milestones?.findIndex((item) => item?.milestone_id === currentMilestone?.milestone_id);
  const nextMilestoneIdx = milestones?.findIndex((item) => item?.milestone_id === nextMilestone?.milestone_id);
  const isSame = currentMilestoneIdx === nextMilestoneIdx;
  return { isSame, currentMilestone, currentMilestoneIdx, nextMilestone, nextMilestoneIdx };
};

const getIsFormAvailable = (values?: IUpdateNextMilestoneFormModel): boolean => {
  const { next_milestone_id: nextMilestoneId } = values || {};
  return !!nextMilestoneId;
};

const handleValidate = (values?: IUpdateNextMilestoneFormModel): Record<string, string> => {
  if (!values) return { next_milestone_id: FIELD_COMMON_ERR_MSG };
  const errors: Record<string, string> = {};
  const entries = Object.entries(values);
  for (const entry of entries) {
    const [key, value] = entry;
    if (isEmptyValue(value?.trim())) {
      errors[key] = FIELD_COMMON_ERR_MSG;
    }
  }
  return errors;
};

const UpdateNextMilestone: React.FC<IUpdateNextMilestoneProps> = (props) => {
  const {
    projectId,
    studioId,
    stage,
    milestones,
    nextMilestone: nextMilestoneProps,
    onUpdateNextMilestoneSuccess,
  } = props;

  const milestoneOptions = getMilestoneOptions(milestones);
  const milestoneTimelines = getMilestoneTimelines(milestones);

  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleConfirm = async () => {
    const {
      milestone_id: nextMilestoneId,
      milestone: nextMilestoneName,
      date: nextMilestoneDate,
    } = nextMilestone || {};
    const { milestone: currentMilestoneName } = currentMilestone || {};
    await updateProjectKeyMilestones({
      project_id: projectId,
      studio_entity_id: studioId,
      current_milestone: currentMilestoneName || '',
      next_milestone_id: nextMilestoneId || '',
      next_milestone: nextMilestoneName || '',
      next_milestone_date: formatDateStr(nextMilestoneDate, 'YYYYMMDD') || '',
    });
    setSubmitted(false);
    setVisible(false);
    form?.restart();
    onUpdateNextMilestoneSuccess?.(nextMilestoneId || '');
  };

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const nextMilestoneField = useField('next_milestone_id', form);

  const formAvailable = getIsFormAvailable(values);

  const { isSame, currentMilestone, currentMilestoneIdx, nextMilestone } = getKeyMilestonesData(
    milestones,
    '',
    values?.next_milestone_id,
  );

  const handleSubmitInPage = () => {
    setSubmitted(true);
    handleSubmit();
  };

  const handleOpen = () => {
    setSubmitted(false);
    setVisible(true);
    form.initialize({ ...DEFAULT_FORM_MODEL, next_milestone_id: nextMilestoneProps?.milestone_id || '' });
  };

  const handleCancel = () => {
    setSubmitted(false);
    setVisible(false);
    form.restart();
  };

  return (
    <>
      <IconButton className="dbg-next-milestone-btn" type="text" onClick={handleOpen}>
        {DEFINE_NEXT_MILESTONE}
      </IconButton>
      <Modal
        className="dbg-next-milestone-modal"
        caption={DEFINE_NEXT_MILESTONE}
        size={800}
        visible={visible}
        onClose={handleCancel}
      >
        <Modal.Body>
          <Form className="dbg-next-milestone-form">
            <Form.Item
              label="Next Milestone"
              showStatusIcon={false}
              status={formatTeaFormItemStatus(submitted, nextMilestoneField?.meta)}
              message={formatTeaFormItemMessage(submitted, nextMilestoneField?.meta)}
            >
              <Select
                {...nextMilestoneField.input}
                className="dbg-next-milestone-select"
                placeholder="Please select"
                appearance="button"
                matchButtonWidth
                options={milestoneOptions}
              />
            </Form.Item>
            <Form.Item label="Progress preview">
              {/* @ts-ignore */}
              <MilestonesTimelines
                timelines={milestoneTimelines}
                isSame={isSame}
                current={currentMilestoneIdx}
                stage={stage}
                getTimelineOffsetMsg={() => {}}
              />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="primary" disabled={!formAvailable} loading={submitting} onClick={handleSubmitInPage}>
            Confirm
          </Button>
          <Button type="weak" onClick={handleCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UpdateNextMilestone;
